<script setup lang="ts">
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendFolderListRequests } from "~/classes/handlers/file/list"

const props = defineProps({
  initialFolder: null,
  foldersOnly: { type: Boolean },
  placeholder: null
})

const emit = defineEmits(["select", "cancel"])
const query = defineModel<string>("query", { default: "" })

const { addInterfaceEventListener, removeInterfaceEventListener } = useEvents()
// const input = useTemplateRef("input")
const currentFolder = ref(props.initialFolder)
const currentFiles = shallowRef<Record<string, StaticFile>>({})
const highlightedFile = ref(-1)
let requestId: string | undefined

// const placeholder = computed(() => props.placeholder ?? "Select a file...")
const sortedFiles = computed(() => {
  const files = Object.values(currentFiles.value)
  return props.foldersOnly
    ? files.filter(file => file.isFolder()).sort((a, b) => a.name()!.localeCompare(b.name()!))
    : files.sort((a, b) => a.name()!.localeCompare(b.name()!))
})

const selectItem = (item: StaticFile) => {
  query.value = ""
  emit("select", item)
}

const onFolderListPageResponse = (response: FolderListPageResponseDatagram) => {
  if (response.folderId !== currentFolder.value.id || response.requestId !== requestId)
    return console.warn("Received folder list response for a different folder or request", response, currentFolder.value.id, requestId)
  if (response.error || !isDefined(response.files))
    return console.error("Error fetching folder list:", response.error)
  for (const file of response.files)
    currentFiles.value[file.id] = new StaticFile(file)
  triggerRef(currentFiles)
}

const drillDown = (folder: StaticFile) => {
  if (requestId)
    removeInterfaceEventListener("folderListPageResponse", onFolderListPageResponse)
  currentFolder.value = folder
  highlightedFile.value = -1
  currentFiles.value = {}
  requestId = generateWorkerRequestId()
  addInterfaceEventListener("folderListPageResponse", onFolderListPageResponse, { requestId })
  sendFolderListRequests([{
    requestId,
    folder: StaticFile.blank(currentFolder.value.id).inner(),
    selection: InterfaceSelection.minimumPathSelection().inner(),
    pathSelection: InterfaceSelection.minimumPathSelection().inner(),
    pageSize: 50,
    offline: false,
  }])
}

const selectCurrentItem = () => {
  if (len(currentFiles.value) === 0 || highlightedFile.value < 0)
    return
  selectItem(currentFiles.value[highlightedFile.value])
}

const highlightItemAbove = () => {
  if (highlightedFile.value <= 0 || highlightedFile.value >= len(currentFiles.value))
    highlightedFile.value = len(currentFiles.value) - 1
  else
    highlightedFile.value--
}

const highlightItemBelow = () => {
  if (highlightedFile.value < 0 || highlightedFile.value >= len(currentFiles.value) - 1)
    highlightedFile.value = 0
  else
    highlightedFile.value++
}

const withCurrentFolderPath = (file: StaticFile) => {
  if (!currentFolder.value.path())
    return file
  const pathPos = currentFolder.value.path()!.findIndex(f => f.id === file.id)
  file.setPath(currentFolder.value.path()!.slice(pathPos + 1))
  return file
}

onMounted(async () => {
  drillDown(props.initialFolder)
  // await nextTick()
  // input.value?.focus()
})

onUnmounted(() => {
  if (requestId)
    removeInterfaceEventListener("folderListPageResponse", onFolderListPageResponse)
})
</script>

<template>
  <dropdown-menu class="file-picker">
    <!-- <menu-section class="menu-select-wrapper">
      <input
        ref="input"
        v-model="query"
        type="text"
        class="menu-select-input"
        :placeholder
        @keydown.enter.stop.prevent="selectCurrentItem()"
        @keydown.esc.stop.prevent="emit('cancel')"
        @keydown.up.prevent="highlightItemAbove()"
        @keydown.down.prevent="highlightItemBelow()"
      >
    </menu-section>
    <menu-separator /> -->
    <interface-scrollbar vertical>
      <menu-section class="file-picker-results">
        <div
          v-for="file in sortedFiles"
          :key="file.id"
          class="file-picker-option"
        >
          <file-item
            :file="file"
            class="file-picker-file"
            @click="selectItem(file)"
          />
          <div class="drill-down" @click="drillDown(file)">
            <icon-action
              type="chevron-right"
              size="xs"
              class="chevron-right"
            />
          </div>
        </div>
      </menu-section>
    </interface-scrollbar>
    <dropdown-chin class="file-picker-chin">
      <div class="file-picker-path">
        <template
          v-for="file in currentFolder.asPath()!.reverse()"
          :key="file.id"
        >
          <div class="separator">
            /
          </div>
          <div
            class="file-picker-path-file"
            @click="drillDown(withCurrentFolderPath(file))"
          >
            {{ file.name() }}
          </div>
        </template>
      </div>
    </dropdown-chin>
  </dropdown-menu>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.file-picker
  @apply min-w-60 flex flex-col

.menu-select-wrapper
  @apply relative flex items-center justify-between flex-none
.menu-select-input
  @apply w-full
  @apply bg-transparent focus:outline-none
  &::placeholder
    @apply text-$global-t-tertiary

.file-picker-results
  @apply max-h-[50vh] min-h-[40px] flex flex-col flex-shrink-1

.file-picker-option
  @apply flex items-center justify-between

.file-picker-file
  @apply cursor-pointer w-full -m-1 mr-0 p-1 rounded-[6px]
  &:hover
    @apply bg-[#{$menu-bg-item-hover}]

.drill-down
  @apply flex-none flex items-center cursor-pointer -m-1 p-1 rounded-[6px]
  &:hover
    @apply bg-[#{$menu-bg-item-hover}]

.file-picker-chin
  @apply flex-none

.file-picker-path
  @apply flex items-center gap-1 overflow-x-hidden px-1 text-$global-t-secondary
  .separator
    @apply text-$global-t-quaternary
    @apply select-none

.file-picker-path-file
  @apply -mx-1 px-1 -my-0.25 py-0.25 rounded-[6px] cursor-pointer flex-none
  &:hover
    @apply bg-[#{$menu-bg-item-hover}]
</style>
